import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Send, ImagePlus, Loader2, MessagesSquare, Search, Plus, Users, X,
  AlertCircle, CheckCircle2, Clock3, UserCog, Hash, Trash2,
  Phone, Mic, Video, Check, CheckCheck,
} from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import { startOutgoingCall } from '../components/CallManager';

const STATUS_META = {
  open:        { label: 'Ouvert',       cls: 'bg-amber-100 text-amber-800 border-amber-200',  icon: AlertCircle },
  in_progress: { label: 'En cours',     cls: 'bg-blue-100 text-blue-800 border-blue-200',     icon: Clock3 },
  resolved:    { label: 'Resolu',       cls: 'bg-emerald-100 text-emerald-800 border-emerald-200', icon: CheckCircle2 },
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TicketBadge = ({ ticket }) => {
  if (!ticket?.is_ticket) return null;
  const s = STATUS_META[ticket.status] || STATUS_META.open;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] border font-medium ${s.cls}`}>
      <Icon className="w-3 h-3" /> {s.label}
    </span>
  );
};

const formatTime = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    return sameDay
      ? d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
      : d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
};

const Chat = () => {
  const { user } = useAuth();
  const [threads, setThreads] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [selected, setSelected] = useState(null); // thread object
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [sending, setSending] = useState(false);
  const [search, setSearch] = useState('');
  const [contactsOpen, setContactsOpen] = useState(false);
  const [ticketOpen, setTicketOpen] = useState(false);
  const [ticketForm, setTicketForm] = useState({ title: '', status: 'open', assigned_to: '' });
  const [typingUsers, setTypingUsers] = useState([]);
  const [presence, setPresence] = useState({});
  const fileRef = useRef(null);
  const scrollRef = useRef(null);
  const lastTypingPingRef = useRef(0);

  const isAdmin = ['admin', 'super_admin', 'agent'].includes(user?.role);

  // === Loaders ===
  const loadThreads = async () => {
    try {
      const r = await api.get('/chat/threads');
      setThreads(r.data || []);
      // If a thread was selected, refresh it from new list
      if (selected) {
        const fresh = (r.data || []).find((t) => t.id === selected.id);
        if (fresh) setSelected(fresh);
      }
    } catch (e) {
      // silent
    }
  };

  const loadContacts = async () => {
    try {
      const r = await api.get('/chat/contacts');
      setContacts(r.data || []);
    } catch { /* ignore */ }
  };

  const loadMessages = async (thread, after = null) => {
    if (!thread) return;
    try {
      const params = after ? { after } : {};
      const r = await api.get(`/chat/threads/${thread.id}/messages`, { params });
      const data = r.data || [];
      if (after) {
        if (data.length) setMessages((prev) => [...prev, ...data]);
      } else {
        setMessages(data);
      }
      return data;
    } catch (e) {
      setMessages([]);
    }
  };

  // === Lifecycle ===
  useEffect(() => {
    loadThreads();
    loadContacts();
    const id = setInterval(loadThreads, 8000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Poll online presence of collaborators (every 30s)
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await api.get('/chat/presence');
        if (cancelled) return;
        const map = {};
        (r.data || []).forEach((p) => { map[p.user_id] = p.online; });
        setPresence(map);
      } catch { /* ignore */ }
    };
    tick();
    const id = setInterval(tick, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // Poll messages for selected thread (full refresh so read statuses update)
  useEffect(() => {
    if (!selected) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await api.get(`/chat/threads/${selected.id}/messages`);
        if (cancelled) return;
        setMessages(r.data || []);
      } catch { /* ignore */ }
    };
    const id = setInterval(tick, 4000);
    return () => { cancelled = true; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  // Mark as read whenever messages list updates and we're viewing the thread
  useEffect(() => {
    if (!selected) return;
    api.post(`/chat/threads/${selected.id}/read`).catch(() => {});
  }, [selected, messages.length]);

  // Poll "is typing..." indicator
  useEffect(() => {
    if (!selected) { setTypingUsers([]); return; }
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await api.get(`/chat/threads/${selected.id}/typing`);
        if (!cancelled) setTypingUsers(r.data || []);
      } catch { if (!cancelled) setTypingUsers([]); }
    };
    tick();
    const id = setInterval(tick, 2500);
    return () => { cancelled = true; clearInterval(id); };
  }, [selected]);

  // Ping "I am typing" while user is composing (throttled)
  const handleInputChange = (val) => {
    setInput(val);
    if (!selected || !val.trim()) return;
    const now = Date.now();
    if (now - lastTypingPingRef.current > 2000) {
      lastTypingPingRef.current = now;
      api.post(`/chat/threads/${selected.id}/typing`).catch(() => {});
    }
  };

  // Auto scroll
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // === Actions ===
  const handleSelect = async (t) => {
    setSelected(t);
    setMessages([]);
    await loadMessages(t);
  };

  const handleStartDM = async (contact) => {
    try {
      const r = await api.post('/chat/threads/dm', { user_id: contact.id });
      setContactsOpen(false);
      await loadThreads();
      handleSelect(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const handleSend = async () => {
    if (!selected || (!input.trim() && !imageFile)) return;
    setSending(true);
    try {
      const fd = new FormData();
      fd.append('body', input);
      if (imageFile) fd.append('image', imageFile);
      const r = await api.post(`/chat/threads/${selected.id}/messages`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setMessages((prev) => [...prev, r.data]);
      setInput('');
      setImageFile(null);
      if (fileRef.current) fileRef.current.value = '';
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur d\'envoi');
    } finally {
      setSending(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const openTicketDialog = () => {
    if (!selected) return;
    const t = selected.ticket;
    setTicketForm({
      title: t?.title || (selected.kind === 'agency' ? 'Probleme equipe' : `Probleme - ${selected.display_name}`),
      status: t?.status || 'open',
      assigned_to: t?.assigned_to || '',
    });
    setTicketOpen(true);
  };

  const handleSaveTicket = async () => {
    if (!ticketForm.title.trim()) return toast.error('Titre requis');
    try {
      const payload = {
        title: ticketForm.title,
        status: ticketForm.status,
        assigned_to: ticketForm.assigned_to || null,
      };
      const r = await api.post(`/chat/threads/${selected.id}/ticket`, payload);
      setSelected(r.data);
      await loadThreads();
      setTicketOpen(false);
      toast.success('Probleme enregistre');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const handleClearTicket = async () => {
    if (!window.confirm('Retirer le marqueur "Probleme" sur cette conversation ?')) return;
    try {
      await api.delete(`/chat/threads/${selected.id}/ticket`);
      const fresh = { ...selected, ticket: null };
      setSelected(fresh);
      await loadThreads();
      toast.success('Probleme retire');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  // === Filtering ===
  const filteredThreads = useMemo(() => {
    const q = search.toLowerCase().trim();
    if (!q) return threads;
    return threads.filter((t) =>
      (t.display_name || '').toLowerCase().includes(q) ||
      (t.last_message_preview || '').toLowerCase().includes(q) ||
      (t.ticket?.title || '').toLowerCase().includes(q)
    );
  }, [threads, search]);

  const totalUnread = useMemo(
    () => threads.reduce((sum, t) => sum + (t.unread_count || 0), 0),
    [threads]
  );

  const memberOptions = useMemo(() => {
    return [{ id: '', name: 'Aucun (non assigne)' }, ...contacts.map((c) => ({ id: c.id, name: c.name })),
      { id: user?.id, name: `${user?.name} (moi)` }];
  }, [contacts, user]);

  return (
    <div className="h-[calc(100vh-100px)] flex flex-col" data-testid="chat-page">
      <div className="mb-3 flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-semibold text-[#1B3A4B] flex items-center gap-2">
            <MessagesSquare className="w-6 h-6 text-[#D4A745]" />
            Messagerie
            {totalUnread > 0 && (
              <span className="inline-flex items-center justify-center min-w-[24px] h-6 px-2 rounded-full bg-red-500 text-white text-xs font-semibold ml-1">
                {totalUnread}
              </span>
            )}
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            Echanges internes et suivi des problemes pour l'agence.
          </p>
        </div>
        <Button
          onClick={() => setContactsOpen(true)}
          className="bg-[#1B3A4B] hover:bg-[#142D3A]"
          data-testid="new-dm-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> Nouvelle conversation
        </Button>
      </div>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-[320px_1fr] xl:grid-cols-[320px_1fr_320px] gap-4 min-h-0">
        {/* Sidebar threads */}
        <Card className="flex flex-col overflow-hidden">
          <div className="p-3 border-b">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-2 top-2.5 text-gray-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher..."
                className="pl-8 h-9"
                data-testid="thread-search"
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {filteredThreads.length === 0 ? (
              <div className="p-6 text-center text-sm text-gray-400">
                Aucune conversation
              </div>
            ) : (
              filteredThreads.map((t) => {
                const isAgency = t.kind === 'agency';
                const isActive = selected?.id === t.id;
                return (
                  <button
                    key={t.id}
                    onClick={() => handleSelect(t)}
                    className={`w-full text-left px-3 py-2.5 border-b border-gray-100 transition flex items-start gap-3 ${
                      isActive ? 'bg-[#FDF7E5]' : 'hover:bg-gray-50'
                    }`}
                    data-testid={`thread-item-${t.id}`}
                  >
                    <div className="relative flex-shrink-0">
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center ${
                        isAgency ? 'bg-[#1B3A4B]' : 'bg-[#D4A745]'
                      }`}>
                        {isAgency ? (
                          <Hash className="w-4 h-4 text-white" />
                        ) : (
                          <span className="text-white text-sm font-semibold">
                            {(t.display_name || '?').charAt(0).toUpperCase()}
                          </span>
                        )}
                      </div>
                      {!isAgency && t.other_user_id && presence[t.other_user_id] && (
                        <span
                          className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-500 border-2 border-white"
                          data-testid={`presence-online-${t.other_user_id}`}
                          title="En ligne"
                        />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5">
                        <p className="font-medium text-sm text-gray-900 truncate flex-1">
                          {t.display_name || 'Conversation'}
                        </p>
                        {t.unread_count > 0 && (
                          <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-semibold">
                            {t.unread_count > 9 ? '9+' : t.unread_count}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 truncate mt-0.5">
                        {t.last_message_preview || 'Aucun message'}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <TicketBadge ticket={t.ticket} />
                        <span className="text-[10px] text-gray-400 ml-auto">{formatTime(t.last_at)}</span>
                      </div>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </Card>

        {/* Messages area */}
        <Card className="flex flex-col overflow-hidden min-h-0">
          {selected ? (
            <>
              {/* Header */}
              <div className="px-4 py-3 border-b flex items-center gap-3 flex-wrap">
                <div className="relative flex-shrink-0">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center ${
                    selected.kind === 'agency' ? 'bg-[#1B3A4B]' : 'bg-[#D4A745]'
                  }`}>
                    {selected.kind === 'agency' ? (
                      <Hash className="w-4 h-4 text-white" />
                    ) : (
                      <span className="text-white text-sm font-semibold">
                        {(selected.display_name || '?').charAt(0).toUpperCase()}
                      </span>
                    )}
                  </div>
                  {selected.kind === 'dm' && selected.other_user_id && presence[selected.other_user_id] && (
                    <span
                      className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-emerald-500 border-2 border-white"
                      title="En ligne"
                    />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900">{selected.display_name}</p>
                  {typingUsers.length > 0 ? (
                    <p
                      className="text-xs text-emerald-600 italic flex items-center gap-1.5"
                      data-testid="typing-indicator"
                    >
                      <span className="inline-flex gap-0.5">
                        <span className="w-1 h-1 rounded-full bg-emerald-500 animate-bounce" style={{ animationDelay: '0ms' }} />
                        <span className="w-1 h-1 rounded-full bg-emerald-500 animate-bounce" style={{ animationDelay: '150ms' }} />
                        <span className="w-1 h-1 rounded-full bg-emerald-500 animate-bounce" style={{ animationDelay: '300ms' }} />
                      </span>
                      {selected.kind === 'agency'
                        ? (typingUsers.length === 1
                            ? `${typingUsers[0].name} ecrit...`
                            : `${typingUsers.length} personnes ecrivent...`)
                        : 'ecrit un message...'}
                    </p>
                  ) : (
                    <p className="text-xs text-gray-500">{selected.display_subtitle}</p>
                  )}
                </div>

                {/* Call buttons (only for DMs) */}
                {selected.kind === 'dm' && selected.other_user_id && (
                  <div className="flex items-center gap-1.5">
                    {selected.other_user_phone && (
                      <a
                        href={`tel:${selected.other_user_phone}`}
                        className="w-9 h-9 rounded-full bg-emerald-50 hover:bg-emerald-100 text-emerald-700 flex items-center justify-center transition border border-emerald-200"
                        title={`Appeler ${selected.other_user_phone}`}
                        data-testid="tel-call-btn"
                      >
                        <Phone className="w-4 h-4" />
                      </a>
                    )}
                    <button
                      type="button"
                      onClick={() => startOutgoingCall({
                        thread_id: selected.id,
                        callee_id: selected.other_user_id,
                        callee_name: selected.display_name,
                        type: 'voice',
                      })}
                      className="w-9 h-9 rounded-full bg-blue-50 hover:bg-blue-100 text-blue-700 flex items-center justify-center transition border border-blue-200"
                      title="Appel vocal in-app"
                      data-testid="voice-call-btn"
                    >
                      <Mic className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => startOutgoingCall({
                        thread_id: selected.id,
                        callee_id: selected.other_user_id,
                        callee_name: selected.display_name,
                        type: 'video',
                      })}
                      className="w-9 h-9 rounded-full bg-violet-50 hover:bg-violet-100 text-violet-700 flex items-center justify-center transition border border-violet-200"
                      title="Appel video in-app"
                      data-testid="video-call-btn"
                    >
                      <Video className="w-4 h-4" />
                    </button>
                  </div>
                )}

                <TicketBadge ticket={selected.ticket} />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={openTicketDialog}
                  className="text-xs"
                  data-testid="open-ticket-dialog-btn"
                >
                  {selected.ticket?.is_ticket ? 'Modifier le probleme' : 'Marquer comme probleme'}
                </Button>
              </div>

              {/* Messages */}
              <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-2 bg-gray-50">
                {messages.length === 0 ? (
                  <div className="text-center text-sm text-gray-400 py-12">
                    Aucun message. Lancez la conversation.
                  </div>
                ) : (
                  messages.map((m) => {
                    const mine = m.sender_id === user?.id;
                    if (m.system) {
                      return (
                        <div key={m.id} className="text-center my-2" data-testid={`msg-${m.id}`}>
                          <span className="text-[11px] text-gray-500 bg-white px-2 py-1 rounded-full border">
                            {m.body} · {formatTime(m.created_at)}
                          </span>
                        </div>
                      );
                    }
                    return (
                      <div
                        key={m.id}
                        className={`flex ${mine ? 'justify-end' : 'justify-start'}`}
                        data-testid={`msg-${m.id}`}
                      >
                        <div
                          className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm shadow-sm ${
                            mine
                              ? 'bg-[#1B3A4B] text-white rounded-br-sm'
                              : 'bg-white border border-gray-200 text-gray-900 rounded-bl-sm'
                          }`}
                        >
                          {!mine && (
                            <p className="text-[11px] font-semibold opacity-70 mb-0.5">
                              {m.sender_name}
                            </p>
                          )}
                          {m.image_url && (
                            <img
                              src={`${BACKEND_URL}${m.image_url}`}
                              alt="attachment"
                              className="rounded-lg max-w-full max-h-72 mb-1 cursor-pointer"
                              onClick={() => window.open(`${BACKEND_URL}${m.image_url}`, '_blank')}
                            />
                          )}
                          {m.body && (
                            <p className="whitespace-pre-wrap break-words">{m.body}</p>
                          )}
                          <p className={`text-[10px] mt-0.5 flex items-center justify-end gap-1 ${mine ? 'text-white/70' : 'text-gray-400'}`}>
                            <span>{formatTime(m.created_at)}</span>
                            {mine && selected.kind === 'dm' && (
                              m.read_by_other ? (
                                <CheckCheck className="w-3 h-3 text-sky-300" data-testid={`msg-status-read-${m.id}`} />
                              ) : (
                                <Check className="w-3 h-3 text-white/60" data-testid={`msg-status-sent-${m.id}`} />
                              )
                            )}
                            {mine && selected.kind === 'agency' && typeof m.read_by_count === 'number' && m.read_by_count > 0 && (
                              <span className="inline-flex items-center gap-0.5 text-[10px]" data-testid={`msg-status-agency-${m.id}`}>
                                <CheckCheck className="w-3 h-3 text-sky-300" /> {m.read_by_count}
                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {/* Composer */}
              <div className="border-t p-3 bg-white">
                {imageFile && (
                  <div className="flex items-center gap-2 mb-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-1.5">
                    <ImagePlus className="w-4 h-4 text-blue-600" />
                    <span className="text-xs text-blue-800 flex-1 truncate">{imageFile.name}</span>
                    <button onClick={() => { setImageFile(null); if (fileRef.current) fileRef.current.value = ''; }} className="text-blue-700 hover:text-blue-900">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                )}
                <div className="flex items-end gap-2">
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={(e) => setImageFile(e.target.files?.[0] || null)}
                    className="hidden"
                    data-testid="chat-file-input"
                  />
                  <Button
                    type="button"
                    size="icon"
                    variant="outline"
                    onClick={() => fileRef.current?.click()}
                    className="flex-shrink-0"
                    data-testid="chat-attach-btn"
                  >
                    <ImagePlus className="w-4 h-4" />
                  </Button>
                  <Textarea
                    value={input}
                    onChange={(e) => handleInputChange(e.target.value)}
                    onKeyDown={handleKey}
                    placeholder="Ecrire un message... (Entree pour envoyer)"
                    rows={1}
                    className="resize-none min-h-[42px] max-h-32"
                    data-testid="chat-input"
                  />
                  <Button
                    onClick={handleSend}
                    disabled={sending || (!input.trim() && !imageFile)}
                    className="bg-[#1B3A4B] hover:bg-[#142D3A] flex-shrink-0"
                    data-testid="chat-send-btn"
                  >
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-center text-gray-400">
              <div>
                <MessagesSquare className="w-12 h-12 mx-auto mb-2 opacity-40" />
                <p className="text-sm">Selectionnez une conversation pour commencer</p>
              </div>
            </div>
          )}
        </Card>

        {/* Right panel : ticket details */}
        <Card className="hidden xl:flex flex-col overflow-hidden">
          <div className="px-4 py-3 border-b">
            <p className="font-semibold text-gray-900 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-[#D4A745]" /> Suivi du probleme
            </p>
          </div>
          <div className="flex-1 overflow-y-auto p-4">
            {!selected ? (
              <p className="text-sm text-gray-400">Aucune conversation selectionnee.</p>
            ) : !selected.ticket?.is_ticket ? (
              <div className="text-sm text-gray-500 space-y-3">
                <p>Cette conversation n'est pas marquee comme un probleme a traiter.</p>
                <Button
                  className="w-full bg-[#1B3A4B] hover:bg-[#142D3A]"
                  onClick={openTicketDialog}
                  data-testid="mark-ticket-btn"
                >
                  <AlertCircle className="w-4 h-4 mr-2" /> Marquer comme probleme
                </Button>
              </div>
            ) : (
              <div className="space-y-4 text-sm">
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Titre</p>
                  <p className="font-semibold text-gray-900">{selected.ticket.title}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Statut</p>
                  <TicketBadge ticket={selected.ticket} />
                </div>
                {selected.ticket.assigned_to_name && (
                  <div>
                    <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Assigne a</p>
                    <p className="text-gray-900 flex items-center gap-1.5">
                      <UserCog className="w-4 h-4 text-gray-500" />
                      {selected.ticket.assigned_to_name}
                    </p>
                  </div>
                )}
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Cree par</p>
                  <p className="text-gray-700">{selected.ticket.opened_by_name} · {formatTime(selected.ticket.opened_at)}</p>
                </div>
                {selected.ticket.resolved_at && selected.ticket.status === 'resolved' && (
                  <div>
                    <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Resolu le</p>
                    <p className="text-emerald-700">{formatTime(selected.ticket.resolved_at)}</p>
                  </div>
                )}
                <div className="flex flex-col gap-2 pt-3 border-t">
                  <Button variant="outline" onClick={openTicketDialog} data-testid="edit-ticket-btn">
                    Modifier le probleme
                  </Button>
                  {isAdmin && (
                    <Button variant="outline" className="text-red-600 hover:text-red-700"
                            onClick={handleClearTicket} data-testid="clear-ticket-btn">
                      <Trash2 className="w-4 h-4 mr-2" /> Retirer le marqueur
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        </Card>
      </div>

      {/* Contacts dialog */}
      <Dialog open={contactsOpen} onOpenChange={setContactsOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-[#D4A745]" /> Demarrer une conversation
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-80 overflow-y-auto space-y-1">
            {contacts.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-6">Aucun autre collaborateur dans votre agence.</p>
            ) : contacts.map((c) => (
              <button
                key={c.id}
                onClick={() => handleStartDM(c)}
                className="w-full text-left px-3 py-2 rounded-lg hover:bg-gray-50 flex items-center gap-3"
                data-testid={`contact-${c.id}`}
              >
                <div className="w-8 h-8 rounded-full bg-[#D4A745] text-white flex items-center justify-center text-sm font-semibold">
                  {(c.name || '?').charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm text-gray-900 truncate">{c.name}</p>
                  <p className="text-xs text-gray-500 truncate">{c.email} · {c.role}</p>
                </div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Ticket dialog */}
      <Dialog open={ticketOpen} onOpenChange={setTicketOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-[#D4A745]" />
              {selected?.ticket?.is_ticket ? 'Modifier le probleme' : 'Marquer comme probleme'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Titre du probleme *</Label>
              <Input
                value={ticketForm.title}
                onChange={(e) => setTicketForm({ ...ticketForm, title: e.target.value })}
                placeholder="Ex : Probleme paiement loyer #L25004"
                data-testid="ticket-title-input"
              />
            </div>
            <div>
              <Label>Statut</Label>
              <Select value={ticketForm.status} onValueChange={(v) => setTicketForm({ ...ticketForm, status: v })}>
                <SelectTrigger data-testid="ticket-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">Ouvert</SelectItem>
                  <SelectItem value="in_progress">En cours</SelectItem>
                  <SelectItem value="resolved">Resolu</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Assigne a (optionnel)</Label>
              <Select value={ticketForm.assigned_to || '__none__'} onValueChange={(v) => setTicketForm({ ...ticketForm, assigned_to: v === '__none__' ? '' : v })}>
                <SelectTrigger data-testid="ticket-assignee-select"><SelectValue placeholder="Aucun" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Aucun</SelectItem>
                  {contacts.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                  {user?.id && <SelectItem value={user.id}>{user.name} (moi)</SelectItem>}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTicketOpen(false)}>Annuler</Button>
            <Button onClick={handleSaveTicket} className="bg-[#1B3A4B] hover:bg-[#142D3A]" data-testid="save-ticket-btn">
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Chat;
